/**
 * The one strict date parser of the date picker (DEVDV-683). It never rolls an impossible date over,
 * never completes a missing part and never guesses a century: only a complete, real date yields an ISO value.
 */

export const EMPTY = 'empty';
export const INCOMPLETE = 'incomplete';
export const INVALID = 'invalid';
export const VALID = 'valid';

const ALLOWED = /^[0-9.\/\-\s]*$/;
const SEPARATORS = /[.\/\-\s]+/;
const ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const ISO_STRICT = /^(\d{4})-(\d{2})-(\d{2})$/;
const PART_LENGTH = {day: 2, month: 2, year: 4};

/**
 * The order and separator of the page language's numeric date, e.g. cs-CZ -> D.M.Y with '.'.
 *
 * @param {string} tag BCP 47 language tag of the page - never the browser's or the OS's
 * @returns {{order: string[], separator: string}}
 */
export function localePattern(tag) {
	const parts = new Intl.DateTimeFormat(tag, {day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC'})
		.formatToParts(new Date(Date.UTC(2000, 10, 22)));
	const order = parts.map((part) => part.type).filter((type) => type in PART_LENGTH);
	const literal = parts.find((part, index) => part.type === 'literal' && index > 0);
	const separator = literal ? literal.value.replace(/\s/g, '') : '';

	// The displayed text has to parse back, so only a separator the parser accepts will do.
	if (order.length !== 3 || separator.length !== 1 || !ALLOWED.test(separator)) {
		throw new Error(`Unsupported date pattern for locale "${tag}"`);
	}

	return {order, separator};
}

/**
 * @param {string} text what the user typed
 * @param {{order: string[], separator: string}} pattern from localePattern()
 * @returns {{state: string, iso?: string, reason?: string}}
 */
export function parseDate(text, pattern) {
	const value = String(text ?? '').trim();

	if (value === '') {
		return {state: EMPTY};
	}

	if (!ALLOWED.test(value)) {
		return {state: INVALID, reason: 'characters'};
	}

	// A pasted ISO date is unambiguous in every language (four-digit year first).
	const iso = ISO.exec(value);

	if (iso) {
		return build({year: iso[1], month: iso[2], day: iso[3]});
	}

	// Bare digits, typed on a phone keypad that has no separator, in the language's order.
	if (/^\d+$/.test(value)) {
		return parseDigits(value, pattern);
	}

	return parseParts(value.split(SEPARATORS).filter((part) => part !== ''), pattern);
}

/**
 * @param {string} iso YYYY-MM-DD
 * @param {{order: string[], separator: string}} pattern from localePattern()
 * @returns {string} the display text, '' for anything that is not a full ISO date
 */
export function formatDate(iso, pattern) {
	const match = ISO_STRICT.exec(iso ?? '');

	if (!match) {
		return '';
	}

	const values = {year: match[1], month: match[2], day: match[3]};

	return pattern.order.map((type) => values[type]).join(pattern.separator);
}

function parseDigits(value, pattern) {
	if (value.length < 8) {
		return {state: INCOMPLETE, reason: 'digits'};
	}

	if (value.length > 8) {
		return {state: INVALID, reason: 'format'};
	}

	const values = {};
	let offset = 0;

	pattern.order.forEach((type) => {
		values[type] = value.slice(offset, offset + PART_LENGTH[type]);
		offset += PART_LENGTH[type];
	});

	return build(values);
}

function parseParts(parts, pattern) {
	if (parts.length > 3 || parts.some((part, index) => part.length > PART_LENGTH[pattern.order[index]])) {
		return {state: INVALID, reason: 'format'};
	}

	const values = {};
	parts.forEach((part, index) => {
		values[pattern.order[index]] = part;
	});

	// A day or month that can never become valid is reported before the rest is typed.
	if (Number(values.day ?? 0) > 31 || Number(values.month ?? 0) > 12) {
		return {state: INVALID, reason: 'range'};
	}

	if (parts.length < 3) {
		return {state: INCOMPLETE, reason: 'missing'};
	}

	if (values.year.length < 4) {
		return {state: INCOMPLETE, reason: 'year4'};
	}

	return build(values);
}

function build({day: dayText, month: monthText, year: yearText}) {
	const day = Number(dayText);
	const month = Number(monthText);
	const year = Number(yearText);

	if (month < 1 || month > 12 || day < 1 || year < 1) {
		return {state: INVALID, reason: 'range'};
	}

	// Round-trip: a rolled-over date (31.02. -> 02.03.) no longer matches what was typed.
	const date = new Date(Date.UTC(2000, 0, 1));
	date.setUTCFullYear(year, month - 1, day);

	if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
		return {state: INVALID, reason: 'impossible'};
	}

	return {
		state: VALID,
		iso:   `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
	};
}
